// src/pages/Admin/Coins.js
import React, { useEffect, useState, useCallback } from 'react';
import adminApi from '../../services/adminApi';
import styles from './Coins.module.css';

const RARITY_OPTIONS = ['bronze', 'silver', 'gold', 'legendary'];
const SOURCE_TYPE_OPTIONS = ['quiz', 'collection', 'manual', 'series_mastery'];

function getRarityClass(rarity) {
    switch (rarity) {
        case 'legendary': return styles.rarityLegendary;
        case 'gold':      return styles.rarityGold;
        case 'silver':    return styles.raritySilver;
        case 'bronze':    return styles.rarityBronze;
        default:          return '';
    }
}

function getRarityEmoji(rarity) {
    switch (rarity) {
        case 'legendary': return '💎';
        case 'gold':      return '🥇';
        case 'silver':    return '🥈';
        case 'bronze':    return '🥉';
        default:          return '🪙';
    }
}

export default function Coins() {
    const [coins, setCoins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        rarity: '',
        source_type: '',
        search: '',
        page: 1,
        limit: 50,
    });
    const [pagination, setPagination] = useState(null);
    const [selectedCoin, setSelectedCoin] = useState(null);

    const fetchCoins = useCallback(async () => {
        try {
            setLoading(true);
            const data = await adminApi.listCoins(filters);
            setCoins(data.coins);
            setPagination(data.pagination);
        } catch (err) {
            console.error('Failed to load coins:', err);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchCoins();
    }, [fetchCoins]);

    return (
        <div className={styles.coins}>
            <div className={styles.header}>
                <h1 className={styles.pageTitle}>🪙 Coins</h1>
                {pagination && (
                    <span className={styles.totalCount}>
                        {pagination.total.toLocaleString()} total
                    </span>
                )}
            </div>

            <div className={styles.filters}>
                <input
                    type="text"
                    placeholder="Search by username or display name..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                    className={styles.searchInput}
                />

                <select
                    value={filters.rarity}
                    onChange={(e) => setFilters(prev => ({ ...prev, rarity: e.target.value, page: 1 }))}
                    className={styles.select}
                >
                    <option value="">All Rarities</option>
                    {RARITY_OPTIONS.map(r => (
                        <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                    ))}
                </select>

                <select
                    value={filters.source_type}
                    onChange={(e) => setFilters(prev => ({ ...prev, source_type: e.target.value, page: 1 }))}
                    className={styles.select}
                >
                    <option value="">All Sources</option>
                    {SOURCE_TYPE_OPTIONS.map(s => (
                        <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                    ))}
                </select>
            </div>

            {loading ? (
                <div className={styles.loading}>Loading coins...</div>
            ) : coins.length === 0 ? (
                <div className={styles.empty}>
                    <div className={styles.emptyIcon}>🪙</div>
                    <div>No coins found</div>
                </div>
            ) : (
                <>
                    <div className={styles.table}>
                        <table>
                            <thead>
                            <tr>
                                <th>Coin</th>
                                <th>User</th>
                                <th>Rarity</th>
                                <th>Source</th>
                                <th>Media / Collection</th>
                                <th>Earned</th>
                                <th></th>
                            </tr>
                            </thead>
                            <tbody>
                            {coins.map(coin => (
                                <tr key={coin.id}>
                                    <td>
                                        <div className={styles.coinCell}>
                                            {coin.coinImageUrl ? (
                                                <img
                                                    src={coin.coinImageUrl}
                                                    alt="coin"
                                                    className={styles.coinThumb}
                                                />
                                            ) : (
                                                <div className={`${styles.coinThumbFallback} ${getRarityClass(coin.rarity)}`}>
                                                    {getRarityEmoji(coin.rarity)}
                                                </div>
                                            )}
                                            <span className={styles.coinId}>#{coin.id}</span>
                                        </div>
                                    </td>
                                    <td>
                                        {coin.user ? (
                                            <div className={styles.userCell}>
                                                {coin.user.avatar_url ? (
                                                    <img
                                                        src={coin.user.avatar_url}
                                                        alt={coin.user.username}
                                                        className={styles.avatar}
                                                    />
                                                ) : (
                                                    <div className={styles.avatarPlaceholder}>
                                                        {coin.user.username?.[0]?.toUpperCase() || '?'}
                                                    </div>
                                                )}
                                                <div className={styles.userInfo}>
                                                    <span className={styles.username}>{coin.user.username}</span>
                                                    {coin.user.display_name && (
                                                        <span className={styles.displayName}>{coin.user.display_name}</span>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <span className={styles.muted}>—</span>
                                        )}
                                    </td>
                                    <td>
                                            <span className={`${styles.rarityBadge} ${getRarityClass(coin.rarity)}`}>
                                                {getRarityEmoji(coin.rarity)} {coin.rarity || '—'}
                                            </span>
                                    </td>
                                    <td>
                                            <span className={styles.sourceType}>
                                                {coin.source_type?.replace(/_/g, ' ') || '—'}
                                            </span>
                                    </td>
                                    <td>
                                        <div className={styles.contextCell}>
                                            {coin.badge?.media ? (
                                                <>
                                                    <span className={styles.mediaTitle}>{coin.badge.media.title}</span>
                                                    <span className={styles.mediaType}>{coin.badge.media.media_type}</span>
                                                </>
                                            ) : coin.collection ? (
                                                <>
                                                    <span className={styles.collectionName}>{coin.collection.name}</span>
                                                    {coin.collection.reward_coin_name && (
                                                        <span className={styles.mediaType}>{coin.collection.reward_coin_name}</span>
                                                    )}
                                                </>
                                            ) : (
                                                <span className={styles.muted}>—</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className={styles.date}>
                                        {coin.createdAt
                                            ? new Date(coin.createdAt).toLocaleDateString()
                                            : '—'}
                                    </td>
                                    <td>
                                        <button
                                            onClick={() => setSelectedCoin(coin)}
                                            className={styles.viewBtn}
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {pagination && pagination.totalPages > 1 && (
                        <div className={styles.pagination}>
                            <button
                                disabled={pagination.page === 1}
                                onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                                className={styles.pageBtn}
                            >
                                Previous
                            </button>
                            <span className={styles.pageInfo}>
                                Page {pagination.page} of {pagination.totalPages}
                            </span>
                            <button
                                disabled={pagination.page === pagination.totalPages}
                                onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                                className={styles.pageBtn}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {selectedCoin && (
                <CoinDetailModal
                    coin={selectedCoin}
                    onClose={() => setSelectedCoin(null)}
                />
            )}
        </div>
    );
}

function CoinDetailModal({ coin: initialCoin, onClose }) {
    const [coin, setCoin] = useState(initialCoin);
    const [refreshing, setRefreshing] = useState(false);

    // Re-fetch fresh signed URL when modal opens
    useEffect(() => {
        let cancelled = false;
        async function refresh() {
            setRefreshing(true);
            try {
                const data = await adminApi.getCoin(initialCoin.id);
                if (!cancelled) setCoin(data.coin);
            } catch (err) {
                console.error('Failed to refresh coin image:', err);
            } finally {
                if (!cancelled) setRefreshing(false);
            }
        }
        refresh();
        return () => { cancelled = true; };
    }, [initialCoin.id]);

    return (
        <div className={styles.modal} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>Coin Detail</h2>
                    <button onClick={onClose} className={styles.closeBtn}>×</button>
                </div>

                <div className={styles.modalBody}>
                    {/* Coin image */}
                    <div className={styles.modalCoinWrap}>
                        {refreshing ? (
                            <div className={styles.modalCoinFallback}>⏳</div>
                        ) : coin.coinImageUrl ? (
                            <img src={coin.coinImageUrl} alt="coin" className={styles.modalCoinImg} />
                        ) : (
                            <div className={`${styles.modalCoinFallback} ${getRarityClass(coin.rarity)}`}>
                                {getRarityEmoji(coin.rarity)}
                            </div>
                        )}
                    </div>

                    <section className={styles.section}>
                        <h3 className={styles.sectionTitle}>Coin Info</h3>
                        <div className={styles.detailGrid}>
                            <div className={styles.detailRow}>
                                <label>ID</label>
                                <span>#{coin.id}</span>
                            </div>
                            <div className={styles.detailRow}>
                                <label>Rarity</label>
                                <span className={`${styles.rarityBadge} ${getRarityClass(coin.rarity)}`}>
                                    {getRarityEmoji(coin.rarity)} {coin.rarity}
                                </span>
                            </div>
                            <div className={styles.detailRow}>
                                <label>Source</label>
                                <span>{coin.source_type?.replace(/_/g, ' ') || '—'}</span>
                            </div>
                            <div className={styles.detailRow}>
                                <label>Earned</label>
                                <span>
                                    {coin.createdAt
                                        ? new Date(coin.createdAt).toLocaleDateString()
                                        : '—'}
                                </span>
                            </div>
                        </div>
                    </section>

                    {coin.user && (
                        <section className={styles.section}>
                            <h3 className={styles.sectionTitle}>Owner</h3>
                            <div className={styles.modalUserRow}>
                                {coin.user.avatar_url ? (
                                    <img
                                        src={coin.user.avatar_url}
                                        alt={coin.user.username}
                                        className={styles.avatarLarge}
                                    />
                                ) : (
                                    <div className={styles.avatarPlaceholderLarge}>
                                        {coin.user.username?.[0]?.toUpperCase() || '?'}
                                    </div>
                                )}
                                <div className={styles.detailGrid}>
                                    <div className={styles.detailRow}>
                                        <label>Username</label>
                                        <span>{coin.user.username}</span>
                                    </div>
                                    {coin.user.display_name && (
                                        <div className={styles.detailRow}>
                                            <label>Display Name</label>
                                            <span>{coin.user.display_name}</span>
                                        </div>
                                    )}
                                    <div className={styles.detailRow}>
                                        <label>User ID</label>
                                        <span className={styles.muted}>{coin.user.id}</span>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}

                    {coin.media && (
                        <section className={styles.section}>
                            <h3 className={styles.sectionTitle}>Media</h3>
                            <div className={styles.detailGrid}>
                                <div className={styles.detailRow}>
                                    <label>Title</label>
                                    <span>{coin.media.title}</span>
                                </div>
                                <div className={styles.detailRow}>
                                    <label>Type</label>
                                    <span>{coin.media.media_type}</span>
                                </div>
                                {coin.media.tmdb_movie_id && (
                                    <div className={styles.detailRow}>
                                        <label>TMDB Movie ID</label>
                                        <span className={styles.muted}>{coin.media.tmdb_movie_id}</span>
                                    </div>
                                )}
                                {coin.media.tmdb_show_id && (
                                    <div className={styles.detailRow}>
                                        <label>TMDB Show ID</label>
                                        <span className={styles.muted}>{coin.media.tmdb_show_id}</span>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    {coin.collection && (
                        <section className={styles.section}>
                            <h3 className={styles.sectionTitle}>Collection</h3>
                            <div className={styles.detailGrid}>
                                <div className={styles.detailRow}>
                                    <label>Name</label>
                                    <span>{coin.collection.name}</span>
                                </div>
                                {coin.collection.reward_coin_name && (
                                    <div className={styles.detailRow}>
                                        <label>Reward Coin</label>
                                        <span>{coin.collection.reward_coin_name}</span>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
}